import React, { useState, useEffect } from 'react';
import { salesCustomersApi, salesInvoicesApi, type CustomerDto, type InvoiceDto } from '../../../services/api';

export type LineItemRow = {
  id: string;
  description: string;
  quantity: number;
  price: number;
  tax_percentage: number;
};

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function lineTotal(qty: number, price: number, taxPct: number): number {
  return Math.round(qty * price * (1 + taxPct / 100) * 100) / 100;
}

interface CreateInvoiceModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** When set, modal opens in edit mode for this invoice */
  invoiceId?: string | null;
}

const defaultTerms = 'Payment due within 30 days';

function emptyForm(): {
  customerId: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  referencePo: string;
  notes: string;
  termsAndConditions: string;
  lines: LineItemRow[];
} {
  const d = new Date();
  const due = new Date(d);
  due.setDate(due.getDate() + 30);
  return {
    customerId: '',
    invoiceNumber: '',
    invoiceDate: d.toISOString().slice(0, 10),
    dueDate: due.toISOString().slice(0, 10),
    referencePo: '',
    notes: '',
    termsAndConditions: defaultTerms,
    lines: [{ id: '1', description: '', quantity: 1, price: 0, tax_percentage: 0 }],
  };
}

function invoiceToForm(inv: InvoiceDto): {
  customerId: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  referencePo: string;
  notes: string;
  termsAndConditions: string;
  lines: LineItemRow[];
} {
  const items = inv.line_items && inv.line_items.length > 0
    ? inv.line_items.map((l, i) => ({
        id: (l as { id?: string }).id ?? String(i + 1),
        description: l.description,
        quantity: l.quantity,
        price: l.price,
        tax_percentage: l.tax_percentage ?? 0,
      }))
    : [{ id: '1', description: '', quantity: 1, price: 0, tax_percentage: 0 }];
  return {
    customerId: inv.customer_id,
    invoiceNumber: inv.invoice_number ?? '',
    invoiceDate: inv.invoice_date.slice(0, 10),
    dueDate: inv.due_date.slice(0, 10),
    referencePo: inv.reference_po_number ?? '',
    notes: inv.notes ?? '',
    termsAndConditions: inv.terms_and_conditions ?? defaultTerms,
    lines: items,
  };
}

export default function CreateInvoiceModal({ open, onClose, onSuccess, invoiceId }: CreateInvoiceModalProps) {
  const [customers, setCustomers] = useState<CustomerDto[]>([]);
  const [customerId, setCustomerId] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  });
  const [dueDate, setDueDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  });
  const [referencePo, setReferencePo] = useState('');
  const [notes, setNotes] = useState('');
  const [termsAndConditions, setTermsAndConditions] = useState(defaultTerms);
  const [lines, setLines] = useState<LineItemRow[]>([
    { id: '1', description: '', quantity: 1, price: 0, tax_percentage: 0 },
  ]);
  const [saving, setSaving] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editStatus, setEditStatus] = useState<'draft' | 'sent' | 'paid'>('draft');

  const isEditMode = Boolean(open && invoiceId);

  useEffect(() => {
    if (open) {
      salesCustomersApi.list().then((res) => {
        if (res.data) setCustomers(res.data);
      });
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (invoiceId) {
      setLoadingEdit(true);
      setError(null);
      salesInvoicesApi
        .get(invoiceId)
        .then((res) => {
          if (res.data) {
            const f = invoiceToForm(res.data);
            setCustomerId(f.customerId);
            setInvoiceNumber(f.invoiceNumber);
            setInvoiceDate(f.invoiceDate);
            setDueDate(f.dueDate);
            setReferencePo(f.referencePo);
            setNotes(f.notes);
            setTermsAndConditions(f.termsAndConditions);
            setLines(f.lines);
            setEditStatus(res.data.status as 'draft' | 'sent' | 'paid');
          }
        })
        .catch(() => setError('Failed to load invoice'))
        .finally(() => setLoadingEdit(false));
    } else {
      const e = emptyForm();
      setCustomerId(e.customerId);
      setInvoiceNumber(e.invoiceNumber);
      setInvoiceDate(e.invoiceDate);
      setDueDate(e.dueDate);
      setReferencePo(e.referencePo);
      setNotes(e.notes);
      setTermsAndConditions(e.termsAndConditions);
      setLines(e.lines);
      setEditStatus('draft');
    }
  }, [open, invoiceId]);

  const updateLine = (id: string, field: keyof LineItemRow, value: string | number) => {
    setLines((prev) =>
      prev.map((l) => (l.id === id ? { ...l, [field]: value } : l))
    );
  };

  const addLine = () => {
    setLines((prev) => [
      ...prev,
      {
        id: String(Date.now()),
        description: '',
        quantity: 1,
        price: 0,
        tax_percentage: 0,
      },
    ]);
  };

  const removeLine = (id: string) => {
    if (lines.length <= 1) return;
    setLines((prev) => prev.filter((l) => l.id !== id));
  };

  const subtotal = lines.reduce((sum, l) => sum + l.quantity * l.price, 0);
  const taxTotal = lines.reduce(
    (sum, l) => sum + (l.quantity * l.price * l.tax_percentage) / 100,
    0
  );
  const total = Math.round((subtotal + taxTotal) * 100) / 100;

  const buildPayload = (status: 'draft' | 'sent' | 'paid') => ({
    customer_id: customerId,
    invoice_number: invoiceNumber.trim() || undefined,
    invoice_date: invoiceDate,
    due_date: dueDate,
    reference_po_number: referencePo.trim() || undefined,
    notes: notes.trim() || undefined,
    terms_and_conditions: termsAndConditions.trim() || defaultTerms,
    status,
    line_items: lines.map((l) => ({
      description: l.description.trim() || 'Item',
      quantity: l.quantity,
      price: l.price,
      tax_percentage: l.tax_percentage,
    })),
  });

  const handleSaveDraft = async () => {
    if (!customerId) {
      setError('Please select a customer.');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const res = await salesInvoicesApi.create(buildPayload('draft'));
      if (res.errors) throw new Error(res.message || 'Validation failed');
      onSuccess();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateAndSend = async () => {
    if (!customerId) {
      setError('Please select a customer.');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const res = await salesInvoicesApi.create(buildPayload('sent'));
      if (res.errors) throw new Error(res.message || 'Validation failed');
      onSuccess();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create and send');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!invoiceId || !customerId) {
      setError('Please select a customer.');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const payload = buildPayload(editStatus);
      const res = await salesInvoicesApi.update(invoiceId, payload);
      if (res.errors) throw new Error(res.message || 'Validation failed');
      onSuccess();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update invoice');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} aria-hidden="true" />
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-xl border border-gray-200">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">
            {isEditMode ? 'Edit Invoice' : 'Create New Invoice'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-6">
          {loadingEdit && (
            <div className="rounded-xl bg-gray-50 border border-gray-200 text-gray-600 px-4 py-3 text-sm">
              Loading invoice...
            </div>
          )}
          {error && (
            <div className="rounded-xl bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
              {error}
            </div>
          )}

          {/* Invoice Details */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Customer <span className="text-red-500">*</span>
              </label>
              <select
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none text-sm"
              >
                <option value="">Select customer</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.company_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Invoice #</label>
              <input
                type="text"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                placeholder="Auto-generated if left blank"
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Date</label>
              <input
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none text-sm"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Reference / PO Number</label>
              <input
                type="text"
                value={referencePo}
                onChange={(e) => setReferencePo(e.target.value)}
                placeholder="Optional reference"
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none text-sm"
              />
            </div>
          </div>

          {/* Line Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Line Items</span>
            </div>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left text-xs font-semibold text-gray-600 py-2.5 px-3">Description</th>
                    <th className="text-left text-xs font-semibold text-gray-600 py-2.5 px-3 w-20">Qty</th>
                    <th className="text-left text-xs font-semibold text-gray-600 py-2.5 px-3 w-28">Price</th>
                    <th className="text-left text-xs font-semibold text-gray-600 py-2.5 px-3 w-20">Tax %</th>
                    <th className="text-right text-xs font-semibold text-gray-600 py-2.5 px-3 w-28">Total</th>
                    <th className="w-10" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {lines.map((line) => (
                    <tr key={line.id}>
                      <td className="py-2 px-3">
                        <input
                          type="text"
                          value={line.description}
                          onChange={(e) => updateLine(line.id, 'description', e.target.value)}
                          placeholder="Item description"
                          className="w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
                        />
                      </td>
                      <td className="py-2 px-3">
                        <input
                          type="number"
                          min={0}
                          step={1}
                          value={line.quantity}
                          onChange={(e) => updateLine(line.id, 'quantity', parseFloat(e.target.value) || 0)}
                          className="w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
                        />
                      </td>
                      <td className="py-2 px-3">
                        <input
                          type="number"
                          min={0}
                          step={0.01}
                          value={line.price}
                          onChange={(e) => updateLine(line.id, 'price', parseFloat(e.target.value) || 0)}
                          className="w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
                        />
                      </td>
                      <td className="py-2 px-3">
                        <input
                          type="number"
                          min={0}
                          max={100}
                          step={0.01}
                          value={line.tax_percentage}
                          onChange={(e) => updateLine(line.id, 'tax_percentage', parseFloat(e.target.value) || 0)}
                          className="w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
                        />
                      </td>
                      <td className="py-2 px-3 text-right text-sm font-medium text-gray-700">
                        {formatCurrency(lineTotal(line.quantity, line.price, line.tax_percentage))}
                      </td>
                      <td className="py-2 px-1">
                        {lines.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeLine(line.id)}
                            className="p-1.5 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600"
                            aria-label="Remove line"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              type="button"
              onClick={addLine}
              className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:text-brand-700"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Line
            </button>
          </div>

          {/* Summary */}
          <div className="flex justify-end">
            <div className="w-56 space-y-1.5 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal</span>
                <span>{formatCurrency(subtotal)}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Tax</span>
                <span>{formatCurrency(taxTotal)}</span>
              </div>
              <div className="flex justify-between text-base font-bold text-brand-600 pt-1 border-t border-gray-200">
                <span>Total</span>
                <span>{formatCurrency(total)}</span>
              </div>
            </div>
          </div>

          {/* Notes & Terms */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Notes visible on invoice"
                rows={3}
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none text-sm resize-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Terms & Conditions</label>
              <textarea
                value={termsAndConditions}
                onChange={(e) => setTermsAndConditions(e.target.value)}
                rows={3}
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none text-sm resize-none"
              />
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-100 px-6 py-4 flex flex-wrap items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50"
          >
            Cancel
          </button>
          {isEditMode ? (
            <>
              <button
                type="button"
                onClick={handleUpdate}
                disabled={saving || loadingEdit}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:opacity-50"
              >
                Update Invoice
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={handleSaveDraft}
                disabled={saving}
                className="px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
              >
                Save Draft
              </button>
              <button
                type="button"
                onClick={handleCreateAndSend}
                disabled={saving}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:opacity-50"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
                Create & Send
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}
